import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, RefreshCw, Image as ImageIcon, BookOpen, Target, Clock, Edit2, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { resolveImage } from '../../utils/imageUrl';

const imgUrl = resolveImage;

/* ── reusable section card ── */
function SectionCard({ icon: Icon, title, color, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50" style={{ background: `${color}08` }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <h2 className="font-semibold text-gray-800 text-sm">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

/* ── image upload preview ── */
function ImageUpload({ label, preview, onChange, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
      <div className="flex items-start gap-4">
        <div className="w-32 h-24 rounded-xl border-2 border-dashed border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
          {preview
            ? <img src={preview} alt="" className="w-full h-full object-cover" />
            : <ImageIcon size={20} className="text-gray-300" />
          }
        </div>
        <div className="flex-1">
          <input type="file" accept="image/*" onChange={onChange}
            className="block w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-medium file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 cursor-pointer" />
          <p className="text-xs text-gray-400 mt-1">JPG, PNG — max 5MB</p>
        </div>
      </div>
    </div>
  );
}

/* ── text field ── */
function TF({ label, value, onChange, placeholder, multiline, rows = 3, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
      {multiline
        ? <textarea value={value} onChange={onChange} rows={rows} placeholder={placeholder}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 resize-none transition-colors" />
        : <input value={value} onChange={onChange} placeholder={placeholder}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
      }
    </div>
  );
}

/* ── save button ── */
function SaveBtn({ loading, onClick, label = 'Save Section' }) {
  return (
    <button onClick={onClick} disabled={loading}
      className="flex items-center gap-2 px-5 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-xl transition-colors disabled:opacity-50 mt-4">
      {loading ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
      {loading ? 'Saving…' : label}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function AdminAboutUs() {
  /* ── hero ── */
  const [hero, setHero]         = useState({ title: '', subtitle: '' });
  const [heroFile, setHeroFile] = useState(null);
  const [heroPrev, setHeroPrev] = useState(null);
  const [heroLoad, setHeroLoad] = useState(false);

  /* ── story ── */
  const [story, setStory]           = useState({ title: '', paragraphs: ['', '', '', ''], extra: '' });
  const [storyEditing, setStoryEditing] = useState(true); // true = edit mode, false = view mode
  const [storyFile, setStoryFile]   = useState(null);
  const [storyPrev, setStoryPrev]   = useState(null);
  const [storyLoad, setStoryLoad]   = useState(false);

  /* ── values (vision/mission/values) ── */
  const [values, setValues]     = useState([
    { title: 'Our Vision', desc: '' },
    { title: 'Our Mission', desc: '' },
    { title: 'Our Values', desc: '' },
  ]);
  const [valLoad, setValLoad]   = useState(false);

  /* ── timeline ── */
  const [timeline, setTimeline] = useState([
    { year: '2004', title: 'Founded', desc: '' },
  ]);
  const [tlLoad, setTlLoad]     = useState(false);

  /* ── stats ── */
  const [stats, setStats]       = useState([
    { value: '20+', label: 'Years of Excellence' },
    { value: '500+', label: 'Products' },
    { value: '50+', label: 'Global Clients' },
  ]);
  const [statsLoad, setStatsLoad] = useState(false);

  /* ── load all ── */
  const load = useCallback(() => {
    api.get('/content?page=about').then(res => {
      const items = res.data.content || [];
      const get = s => items.find(c => c.section === s);

      const h = get('hero');
      if (h) {
        setHero({ title: h.title || '', subtitle: h.subtitle || '' });
        setHeroPrev(imgUrl(h.image));
      }

      const st = get('story');
      if (st) {
        // Prefer extraData.paragraphs (new format) over splitting body (old format)
        let paras = [];
        let extra = '';

        if (st.extraData?.paragraphs?.length) {
          // New format — paragraphs stored as array
          paras = st.extraData.paragraphs;
          extra = st.extraData.extra || '';
        } else if (st.body) {
          // Old format — split on double newline
          const parts = st.body.split('\n\n');
          paras = parts.slice(0, 4).filter(Boolean);
          extra = parts.slice(4).join('\n\n');
        }

        // Ensure at least 1 paragraph slot
        if (paras.length === 0) paras = [''];

        setStory({ title: st.title || '', paragraphs: paras, extra });
        setStoryPrev(imgUrl(st.image));
        setStoryEditing(false);
      }

      const v = get('values');
      if (v?.extraData?.items) setValues(v.extraData.items);

      const tl = get('timeline');
      if (tl?.extraData?.items) setTimeline(tl.extraData.items);

      const s = get('stats');
      if (s?.extraData?.items) setStats(s.extraData.items);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── save helpers ── */
  const saveSection = async (section, fields, file, fileField = 'image', setLoad) => {
    setLoad(true);
    try {
      const fd = new FormData();
      fd.append('page', 'about');
      fd.append('section', section);
      Object.entries(fields).forEach(([k, v]) =>
        fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v)
      );
      if (file) fd.append(fileField, file);
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${section} section saved`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving');
    } finally { setLoad(false); }
  };

  const saveHero = () => saveSection('hero',
    { title: hero.title, subtitle: hero.subtitle },
    heroFile, 'image', setHeroLoad);

  const saveStory = async () => {
    // Store paragraphs as a JSON array to preserve each paragraph independently
    const cleanParagraphs = story.paragraphs.map(p => p.trim()).filter(Boolean);
    // Also keep body for backward compat (joined)
    const bodyParts = [...cleanParagraphs];
    if (story.extra.trim()) bodyParts.push(story.extra.trim());

    await saveSection('story',
      {
        title: story.title,
        body: bodyParts.join('\n\n'),
        extraData: JSON.stringify({
          paragraphs: cleanParagraphs,
          extra: story.extra.trim(),
        }),
      },
      storyFile, 'image', setStoryLoad);
    setStoryEditing(false);
  };

  const saveValues = () => saveSection('values',
    { extraData: JSON.stringify({ items: values }) },
    null, null, setValLoad);

  const saveTimeline = () => saveSection('timeline',
    { extraData: JSON.stringify({ items: timeline }) },
    null, null, setTlLoad);

  const saveStats = () => saveSection('stats',
    { extraData: JSON.stringify({ items: stats }) },
    null, null, setStatsLoad);

  /* ── timeline helpers ── */
  const addTl = () => setTimeline(t => [...t, { year: '', title: '', desc: '' }]);
  const removeTl = i => setTimeline(t => t.filter((_, idx) => idx !== i));
  const updateTl = (i, k, v) => setTimeline(t => t.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  /* ── stats helpers ── */
  const addStat = () => setStats(s => [...s, { value: '', label: '' }]);
  const removeStat = i => setStats(s => s.filter((_, idx) => idx !== i));
  const updateStat = (i, k, v) => setStats(s => s.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  return (
    <div className="space-y-6 pb-8">
      {/* page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">About Us</h1>
          <p className="text-sm text-gray-400 mt-0.5">Edit every section of the About Us page</p>
        </div>
        <button onClick={load}
          className="flex items-center gap-1.5 text-xs px-4 py-2 border border-gray-200 rounded-xl hover:border-yellow-400 hover:text-yellow-600 transition-colors bg-white shadow-sm">
          <RefreshCw size={12} /> Reload
        </button>
      </div>

      {/* ── Hero Section ── */}
      <SectionCard icon={ImageIcon} title="Hero Banner" color="#4F8EF7">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <TF label="Page Label" value={hero.subtitle} onChange={e => setHero({ ...hero, subtitle: e.target.value })}
              placeholder="e.g. Who We Are" hint="Small text above the title" />
            <TF label="Hero Title" value={hero.title} onChange={e => setHero({ ...hero, title: e.target.value })}
              placeholder="e.g. About Us" />
          </div>
          <ImageUpload label="Hero Background Image" preview={heroPrev}
            onChange={e => { const f = e.target.files[0]; setHeroFile(f); setHeroPrev(f ? URL.createObjectURL(f) : heroPrev); }}
            hint="Recommended: 1920×500px landscape" />
        </div>
        <SaveBtn loading={heroLoad} onClick={saveHero} />
      </SectionCard>

      {/* ── Our Story ── */}
      <SectionCard icon={BookOpen} title="Our Story" color="#10B981">

        {/* ── VIEW MODE ── */}
        {!storyEditing && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Section Title</p>
                <p className="text-base font-semibold text-gray-800">{story.title || <span className="italic text-gray-300">No title set</span>}</p>
              </div>
              <button onClick={() => setStoryEditing(true)}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-xl transition-colors">
                <Edit2 size={12} /> Edit Section
              </button>
            </div>
            {storyPrev && (
              <div className="flex items-start gap-4 pt-2">
                <img src={storyPrev} alt="Story" className="w-24 h-20 object-cover rounded-xl border border-gray-100" />
                <div className="flex-1 space-y-2">
                  {story.paragraphs.filter(p => p.trim()).map((p, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-gray-400 mb-1 font-semibold uppercase tracking-wider">Paragraph {i + 1}</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{p}</p>
                    </div>
                  ))}
                  {story.extra.trim() && (
                    <div className="bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
                      <p className="text-xs text-emerald-600 mb-1 font-semibold uppercase tracking-wider">Additional Content</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{story.extra}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!storyPrev && (
              <div className="space-y-2">
                {story.paragraphs.filter(p => p.trim()).map((p, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 mb-1 font-semibold uppercase tracking-wider">Paragraph {i + 1}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{p}</p>
                  </div>
                ))}
                {story.extra.trim() && (
                  <div className="bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
                    <p className="text-xs text-emerald-600 mb-1 font-semibold uppercase tracking-wider">Additional Content</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{story.extra}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── EDIT MODE ── */}
        {storyEditing && (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-6">
              {/* left: title + paragraphs */}
              <div className="space-y-4">
                {/* Section title input */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Section Title
                  </label>
                  <input
                    value={story.title}
                    onChange={e => setStory({ ...story, title: e.target.value })}
                    placeholder="e.g. Crafting Excellence Since 2004"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400 transition-colors bg-white"
                  />
                </div>

                {/* Dynamic paragraphs (max 4) */}
                {story.paragraphs.map((para, i) => (
                  <div key={i} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Paragraph {i + 1}
                        {i === 0 && <span className="ml-1 text-red-400">*</span>}
                      </label>
                      {i > 0 && (
                        <button
                          onClick={() => setStory(s => ({ ...s, paragraphs: s.paragraphs.filter((_, idx) => idx !== i) }))}
                          className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors">
                          <Trash2 size={11} /> Remove
                        </button>
                      )}
                    </div>
                    <textarea
                      value={para}
                      onChange={e => setStory(s => ({ ...s, paragraphs: s.paragraphs.map((p, idx) => idx === i ? e.target.value : p) }))}
                      rows={4}
                      placeholder={`Enter paragraph ${i + 1} content here…`}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400 resize-none transition-colors bg-white"
                    />
                  </div>
                ))}

                {/* Add paragraph button (max 4) */}
                {story.paragraphs.length < 4 && (
                  <button
                    onClick={() => setStory(s => ({ ...s, paragraphs: [...s.paragraphs, ''] }))}
                    className="flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2 rounded-xl transition-colors w-full justify-center">
                    <Plus size={13} /> Add Paragraph
                    <span className="text-emerald-400 font-normal">({story.paragraphs.length}/4)</span>
                  </button>
                )}
                {story.paragraphs.length >= 4 && (
                  <p className="text-xs text-gray-400 text-center py-1">Maximum 4 paragraphs reached</p>
                )}

                {/* Additional content textarea */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    Additional Content
                    <span className="ml-2 text-gray-400 font-normal normal-case">(optional — extra text below paragraphs)</span>
                  </label>
                  <textarea
                    value={story.extra}
                    onChange={e => setStory({ ...story, extra: e.target.value })}
                    rows={4}
                    placeholder="Enter any additional content, notes, or supplementary text here…"
                    className="w-full border border-dashed border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400 resize-none transition-colors bg-gray-50"
                  />
                </div>
              </div>

              {/* right: image upload */}
              <ImageUpload label="Story Image" preview={storyPrev}
                onChange={e => { const f = e.target.files[0]; setStoryFile(f); setStoryPrev(f ? URL.createObjectURL(f) : storyPrev); }}
                hint="Recommended: square, min 600×600px" />
            </div>

            {/* action buttons */}
            <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
              <button onClick={saveStory} disabled={storyLoad}
                className="flex items-center gap-2 px-5 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-xl transition-colors disabled:opacity-50">
                {storyLoad ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                {storyLoad ? 'Saving…' : 'Save Story Section'}
              </button>
              {story.title && (
                <button onClick={() => setStoryEditing(false)}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                  <CheckCircle size={12} /> Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </SectionCard>

      {/* ── Stats ── */}
      <SectionCard icon={Target} title="Stats Bar" color="#F59E0B">
        <p className="text-xs text-gray-400 mb-4">Numbers displayed below the hero (e.g. 20+ Years, 500+ Products)</p>
        <div className="space-y-3">
          {stats.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <input value={s.value} onChange={e => updateStat(i, 'value', e.target.value)}
                placeholder="e.g. 20+" className="w-28 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400" />
              <input value={s.label} onChange={e => updateStat(i, 'label', e.target.value)}
                placeholder="e.g. Years of Excellence" className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400" />
              <button onClick={() => removeStat(i)} className="p-2 text-gray-300 hover:text-red-500 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={addStat}
            className="flex items-center gap-1.5 text-xs text-yellow-600 hover:text-yellow-700 font-medium">
            <Plus size={13} /> Add Stat
          </button>
        </div>
        <SaveBtn loading={statsLoad} onClick={saveStats} />
      </SectionCard>

      {/* ── Vision / Mission / Values ── */}
      <SectionCard icon={Target} title="Our Foundation (Vision · Mission · Values)" color="#8B5CF6">
        <div className="grid md:grid-cols-3 gap-4">
          {values.map((v, i) => (
            <div key={i} className="space-y-2 p-4 bg-gray-50 rounded-xl">
              <input value={v.title} onChange={e => setValues(vals => vals.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                placeholder="Card title" className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus:border-yellow-400" />
              <textarea value={v.desc} onChange={e => setValues(vals => vals.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))}
                rows={4} placeholder="Description…"
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-yellow-400 resize-none" />
            </div>
          ))}
        </div>
        <SaveBtn loading={valLoad} onClick={saveValues} />
      </SectionCard>

      {/* ── Timeline ── */}
      <SectionCard icon={Clock} title="Milestones Timeline" color="#F43F5E">
        <p className="text-xs text-gray-400 mb-4">Company milestones shown in the timeline section</p>
        <div className="space-y-3">
          {timeline.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <div className="flex flex-col gap-2 shrink-0">
                <input value={item.year} onChange={e => updateTl(i, 'year', e.target.value)}
                  placeholder="Year" className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-bold text-yellow-600 focus:outline-none focus:border-yellow-400 text-center" />
              </div>
              <div className="flex-1 space-y-2">
                <input value={item.title} onChange={e => updateTl(i, 'title', e.target.value)}
                  placeholder="Milestone title" className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus:border-yellow-400" />
                <textarea value={item.desc} onChange={e => updateTl(i, 'desc', e.target.value)}
                  rows={2} placeholder="Description…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-yellow-400 resize-none" />
              </div>
              <button onClick={() => removeTl(i)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors mt-1">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={addTl}
            className="flex items-center gap-1.5 text-xs text-yellow-600 hover:text-yellow-700 font-medium">
            <Plus size={13} /> Add Milestone
          </button>
        </div>
        <SaveBtn loading={tlLoad} onClick={saveTimeline} />
      </SectionCard>
    </div>
  );
}
